import { zodResolver } from "@hookform/resolvers/zod";
import CricketFlag from "../../_components/CricketFlag";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { Button } from "@components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form";
import { Input } from "@components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { COUNTRIES } from "@utils/constants/countries";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Minus } from "lucide-react";
import { useState } from "react";
import { usePathname } from "@utils/hook/index.hook";
import { CricketTeamPost, CricketTeamUpdate } from "@services/CricketTeam";
import { CricketTeamType } from "../column/index.column";

const teamSchema = z.object({
  name: z.string().min(1, "Team name is required"),
  iso_code: z.string().min(1, "ISO Code is required"),
  players: z
    .array(
      z.object({
        name: z.string().min(1, "Player name is required"),
      }),
    )
    .min(1, "At least one player is required"),
});

type TeamFormValues = z.infer<typeof teamSchema>;

interface CricketTeamFormProps {
  formdata?: CricketTeamType;
  setOpen?: (open: boolean) => void;
}

export default function CricketTeamForm({
  formdata,
  setOpen,
}: CricketTeamFormProps) {
  const navigate = useNavigate();
  const [playerName, setPlayerName] = useState("");
  const pathname = usePathname();
  const form = useForm<TeamFormValues>({
    resolver: zodResolver(teamSchema),
    values: {
      name: formdata?.name || "",
      iso_code: formdata?.iso_code || "",
      players: formdata?.players || [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "players",
  });

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: any) =>
      formdata ? CricketTeamUpdate(formdata.id, data) : CricketTeamPost(data),
    onSuccess: () => {
      toast.success(
        formdata ? "Team updated successfully" : "Team created successfully",
      );
      form.reset();
      setPlayerName("");
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing`],
      });
      if (setOpen) {
        setOpen(false);
      } else {
        navigate(-1);
      }
    },
    onError: (error) => {
      toast.error(formdata ? "Failed to update team" : "Failed to create team");
      console.error(error);
    },
  });

  const onSubmit = (data: TeamFormValues) => {
    // Transform players array to just names
    const payload = {
      ...data,
      players: data.players?.map((player) => player.name) || [],
    };
    mutate(payload);
  };

  const handleAddPlayer = () => {
    if (!playerName.trim()) return;
    append({ name: playerName });
    setPlayerName("");
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="min-h-[60vh] max-h-[70vh] overflow-y-auto space-y-6 p-1"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Team Name <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter team name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="iso_code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Country (ISO Code) <span className="text-red-500">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      <div className="flex items-center gap-2">
                        <CricketFlag
                          isoCode={country.code}
                          className="w-5 h-5 shadow-sm rounded-sm"
                        />
                        <span>{country.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Players Section */}
        <div className="space-y-4">
          <div className="space-y-2">
            <FormLabel>Player Name</FormLabel>
            <Input
              placeholder="Player Name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddPlayer();
                }
              }}
            />
          </div>

          <Button
            type="button"
            onClick={handleAddPlayer}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Add Player
          </Button>

          <div className="border rounded-lg p-4 mt-4">
            <h3 className="text-sm font-medium mb-3 text-emerald-700">
              Player Name
            </h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {fields.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No players available
                </p>
              ) : (
                fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-md group"
                  >
                    <span className="text-sm font-medium">
                      {form.getValues(`players.${index}.name`)}
                    </span>
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full p-1 transition-colors"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={isPending}>
          {isPending ? "Saving..." : formdata ? "Update Team" : "Save Team"}
        </Button>
      </form>
    </Form>
  );
}
